import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRightLeft, Layers, Play, Plus, MoreHorizontal, Loader2, ExternalLink, ChevronDown, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useConfirm } from "@/components/ConfirmDialog";
import { MoveDialog } from "@/components/MoveDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { Pagination } from "@/components/Pagination";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { NextRunsList } from "@/components/CountdownCell";
import { RefBadge } from "@/components/RefBadge";

function SuiteDialog({ open, onOpenChange, onSaved, initial, projectId, environments = [] }) {
  // Component is remounted by parent (via `key` prop) whenever `initial` changes,
  // so useState initialisers correctly capture the new defaults — no useEffect reset needed.
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [envId, setEnvId] = useState(initial?.default_environment_id || environments[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!initial;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Suite name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        default_environment_id: envId || null,
      };
      if (isEdit) {
        await api.put(`/suites/${initial.id}`, payload);
        toast.success("Suite updated");
      } else {
        await api.post(`/projects/${projectId}/suites`, payload);
        toast.success("Suite created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-xl border-[#E5E7EB]" data-testid="suite-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
            {isEdit ? "Edit suite" : "New suite"}
          </DialogTitle>
          <DialogDescription>
            Suites group tests that should run together — e.g. Smoke, Regression, Auth.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Suite name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Smoke Tests"
              data-testid="suite-name-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="suite-description-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Tags (comma separated)</label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="critical, daily"
              data-testid="suite-tags-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            />
          </div>
          {environments.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">Default environment</label>
              <Select value={envId} onValueChange={setEnvId}>
                <SelectTrigger className="rounded-md border-[#E5E7EB] focus:ring-[#3B82F6]" data-testid="suite-environment-select">
                  <SelectValue placeholder="Select an environment" />
                </SelectTrigger>
                <SelectContent className="rounded-md border-[#E5E7EB]">
                  {environments.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`suite-environment-option-${e.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.type_color || "#64748B" }} />
                        <span>{e.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{e.type}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#94A3B8]">Running this suite will execute every test inside it against this environment. You can override per run.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button
            onClick={handleSave}
            disabled={submitting || !name.trim()}
            data-testid="suite-submit-button"
            className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-1.5"
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            {isEdit ? "Save changes" : "Create suite"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectSuitesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [suites, setSuites] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedulingSuite, setSchedulingSuite] = useState(null);
  const [schedulesByEntity, setSchedulesByEntity] = useState({});
  const [movingSuite, setMovingSuite] = useState(null);
  const { current } = useWorkspace();
  const canMove = current?.is_personal === true && (current?.my_role === "owner" || current?.my_role === "editor");

  const loadActiveSchedules = useCallback(() => {
    api.get(`/schedules?status=active&project_id=${projectId}`)
      .then((r) => {
        const grouped = {};
        for (const s of r.data.items || []) {
          if (!grouped[s.entity_id]) grouped[s.entity_id] = [];
          grouped[s.entity_id].push(s);
        }
        setSchedulesByEntity(grouped);
      })
      .catch(() => {});
  }, [projectId]);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/suites`)
      .then((r) => setSuites(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId]);

  const loadEnvironments = useCallback(() => {
    api.get(`/projects/${projectId}/environments`)
      .then((r) => setEnvironments(r.data.items || []))
      .catch(() => {});
  }, [projectId]);

  useEffect(load, [load]);
  useEffect(loadEnvironments, [loadEnvironments]);
  useEffect(loadActiveSchedules, [loadActiveSchedules]);

  const envById = (id) => environments.find((e) => e.id === id);

  const handleRun = async (s, environmentId = null) => {
    setRunningId(s.id);
    try {
      const qs = environmentId ? `?environment_id=${environmentId}` : "";
      const { data } = await api.post(`/suites/${s.id}/run${qs}`);
      toast.success(`Running ${data.total_count} test${data.total_count === 1 ? "" : "s"}`);
      navigate(`/suite-runs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunningId(null);
    }
  };

  const handleDelete = async (s) => {
    const ok = await confirm({
      title: "Move this suite to the bin?",
      description: `"${s.name}" will be moved to the workspace recycle bin.${s.test_count > 0 ? ` Its ${s.test_count} test(s) will be moved along with it.` : ""} You can restore them within 30 days.`,
      confirmLabel: "Move to bin",
      danger: true,
      testId: "confirm-delete-suite",
    });
    if (!ok) return;
    try {
      await api.delete(`/suites/${s.id}`);
      toast.success("Suite moved to bin");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    {
      key: "name",
      label: "Suite",
      mobileVariant: "title",
      render: (s) => (
        <Link
          to={`/projects/${projectId}/suites/${s.id}`}
          data-testid={`suite-link-${s.id}`}
          className="flex flex-col rt-primary hover:text-[#3B82F6] transition-colors"
        >
          <RefBadge value={s.ref} testId={`suite-ref-${s.id}`} />
          <span className="mt-1">{s.name}</span>
          {s.description && (
            <span className="text-xs text-[#64748B] mt-0.5 line-clamp-1 font-normal">{s.description}</span>
          )}
          {s.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {s.tags.map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569] font-normal">{t}</span>
              ))}
            </div>
          )}
        </Link>
      ),
    },
    { key: "tests", label: "Tests", render: (s) => <span className="text-[#334155]">{s.test_count}</span> },
    {
      key: "environment",
      label: "Environment",
      render: (s) => {
        const env = envById(s.default_environment_id);
        if (!env) return <span className="text-xs text-[#94A3B8]">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5 text-xs text-[#334155]" data-testid={`suite-env-${s.id}`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: env.type_color || "#64748B" }} />
            {env.name}
          </span>
        );
      },
    },
    {
      key: "last_run",
      label: "Last run",
      render: (s) => {
        const upcoming = schedulesByEntity[s.id] || [];
        return (
          <div className="flex flex-col gap-0">
            {s.last_run ? (
              <div className="inline-flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <StatusBadge status={s.last_run.status} />
                  <span className="text-xs text-[#64748B]">{s.last_run.passed_count}/{s.last_run.total_count} passed</span>
                </div>
                <span className="text-[11px] text-[#94A3B8]" data-testid={`suite-last-run-at-${s.id}`}>
                  {s.last_run.started_at ? formatDateTime(s.last_run.started_at) : ""}
                </span>
              </div>
            ) : (
              <span className="text-xs text-[#94A3B8]">No runs yet</span>
            )}
            <NextRunsList schedules={upcoming} testIdPrefix={`suite-next-${s.id}`} />
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (s) => {
        const defaultEnv = envById(s.default_environment_id);
        return (
          <div className="flex items-center justify-end gap-2">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => handleRun(s)}
                disabled={runningId === s.id || s.test_count === 0}
                data-testid={`run-suite-button-${s.id}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 ${environments.length > 1 ? "rounded-l-md" : "rounded-md"} bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors disabled:opacity-50`}
              >
                {runningId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                {defaultEnv ? `Run on ${defaultEnv.name}` : "Run suite"}
              </button>
              {environments.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`run-suite-env-trigger-${s.id}`}
                      disabled={runningId === s.id || s.test_count === 0}
                      className="inline-flex items-center justify-center w-7 h-8 rounded-r-md bg-[#3B82F6] text-white border-l border-[#2563EB] hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                      aria-label="Pick an environment to run on"
                    >
                      <ChevronDown size={12} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[200px]">
                    <DropdownMenuLabel className="text-[11px] text-[#94A3B8] uppercase tracking-wide">Run this suite on…</DropdownMenuLabel>
                    {environments.map((e) => (
                      <DropdownMenuItem
                        key={e.id}
                        onClick={() => handleRun(s, e.id)}
                        data-testid={`run-suite-on-env-${s.id}-${e.id}`}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: e.type_color || "#64748B" }} />
                        <span className="flex-1">{e.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] ml-2">{e.type}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`suite-row-actions-${s.id}`} aria-label="Row actions" className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]">
                  <MoreHorizontal size={14} className="text-[#64748B]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-lg border-[#E5E7EB] p-1 w-44">
                <DropdownMenuItem onClick={() => { setEditing(s); setEditOpen(true); }} className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]" data-testid={`suite-edit-${s.id}`}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setSchedulingSuite(s); setScheduleOpen(true); }}
                  data-testid={`schedule-suite-${s.id}`}
                  className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                >
                  <CalendarClock size={12} className="mr-2 text-[#3B82F6]" />
                  Schedule…
                </DropdownMenuItem>
                {canMove && (
                  <DropdownMenuItem
                    onClick={() => setMovingSuite(s)}
                    data-testid={`suite-move-${s.id}`}
                    className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                  >
                    <ArrowRightLeft size={12} className="mr-2 text-[#475569]" />
                    Move to workspace…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleDelete(s)} className="rounded-md px-2 py-2 text-sm cursor-pointer text-[#B91C1C] focus:bg-[#FEF2F2]" data-testid={`suite-delete-${s.id}`}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="project-suites-page">
      <PageHeader
        title="Test Suites"
        description="Group tests into suites and run them together as Smoke, Regression, Authentication, etc."
        actions={
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="new-suite-button"
            className="inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
          >
            <Plus size={15} />
            New suite
          </button>
        }
      />

      {suites === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : suites.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-suites-message">
          <Layers size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No suites yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Create a suite to group related tests — e.g. Smoke, Regression, or Authentication.
          </p>
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="suites-table"
            columns={columns}
            rows={suites.slice((page - 1) * PER_PAGE, page * PER_PAGE)}
            getRowTestId={(s) => `suite-row-${s.id}`}
          />
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={suites.length}
            onChange={setPage}
            testId="suites-pagination"
          />
        </>
      )}

      <SuiteDialog key={`create-${createOpen}`} open={createOpen} onOpenChange={setCreateOpen} onSaved={load} projectId={projectId} environments={environments} />
      <SuiteDialog key={`edit-${editing?.id || "none"}-${editOpen}`} open={editOpen} onOpenChange={setEditOpen} onSaved={load} initial={editing} projectId={projectId} environments={environments} />

      <ScheduleDialog
        key={`schedule-${schedulingSuite?.id || "none"}-${scheduleOpen}`}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        entityType="suite"
        entityId={schedulingSuite?.id}
        entityName={schedulingSuite?.name}
        environments={environments}
        defaultEnvironmentId={schedulingSuite?.default_environment_id || environments[0]?.id}
      />

      {movingSuite && (
        <MoveDialog
          open={!!movingSuite}
          onOpenChange={(o) => !o && setMovingSuite(null)}
          kind="suite"
          entity={movingSuite}
          onMoved={load}
        />
      )}
    </div>
  );
}
